import './style.scss'
import ReactDOM from 'react-dom/client'
import { Canvas } from '@react-three/fiber'
import Experience from './Experience.tsx'
import * as THREE from "three"
import Parameters from './Components/Parameters/Parameters.tsx'
import { useState, useEffect } from 'react'
import { Chronometer } from './Components/Chronometer/Chronometer.tsx'
import { timePerKmToSeconds, secondsToTimePerKm } from './utils/TransformTime.tsx'
import Loader from './Components/Loader/Loader.tsx'
import CarbonInfo from './Components/CarbonInfo/CarbonInfo.tsx'

const App = (): React.JSX.Element => {

  // --- États ---
  const [distance, setDistance] = useState<number>(1000)
  const [timePerKm, setTimePerKm] = useState<string>("03:00")
  const [lane, setLane] = useState<number>(1)
  const [time, setTime] = useState<number>(() => {
    const pace = timePerKmToSeconds("03:00")
    const val = pace ? Math.round(pace * (1000 / 1000)) : 0
    return val
  })
  const [isPlay, setPlay] = useState<boolean>(false)
  const [cameraLocked, setCameraLocked] = useState<boolean>(true)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  // --- Handlers ---
  const handleClickCamera = () => {
    setCameraLocked(!cameraLocked)
  }

  const onChange = (name: string, value: number | string) => {
    setPlay(false)
    if (name === "distance") setDistance(value as number)
    else if (name === "lane") setLane(value as number)
    else if (name === "time") setTime(value as number)
  }

  const handleClick = () => {
    setPlay(prev => !prev)
  }

  // --- useEffect : mise à jour du temps total ---
  useEffect(() => {
    const paceSec = timePerKmToSeconds(timePerKm)
    if (paceSec == null) {
      return
    }
    const km = Math.max(0.001, distance / 1000)
    const total = Math.round(paceSec * km)
    if (total !== time) {
      setTime(total)
      setPlay(false)
    }
  }, [timePerKm, distance])

  // --- useEffect : mise à jour du pace ---
  useEffect(() => {
    if (distance <= 0) {
      return
    }
    const km = distance / 1000
    const paceSec = time / km
    const paceRounded = Math.round(paceSec)
    const formatted = secondsToTimePerKm(paceRounded)
    if (formatted !== timePerKm) {
      setTimePerKm(formatted)
    }
  }, [time])

  return (
    <>
      {!isLoaded && <Loader setIsLoaded={setIsLoaded} />}

      <Canvas
        gl={{
          antialias: true,
          outputEncoding: THREE.sRGBEncoding
        }}
        shadows
        className={"canvas"}
        camera={{ fov: 45, near: 0.1, far: 200, position: [-7, 2, -9] }}
      >
        <Experience
          distance={distance}
          timePerKm={timePerKm}
          lane={lane}
          time={time}
          isPlay={isPlay}
          setPlay={setPlay}
          cameraLocked={cameraLocked}
        />
      </Canvas>

      <Parameters
        distance={distance}
        setDistance={setDistance}
        lane={lane}
        setLane={setLane}
        time={time}
        setTime={setTime}
        timePerKm={timePerKm}
        setTimePerKm={setTimePerKm}
        isPlay={isPlay}
        setPlay={setPlay}
        onChange={onChange}
        handleClick={handleClick}
        handleClickCamera={handleClickCamera}
        cameraLocked={cameraLocked}
      />
      <Chronometer isPlay={isPlay} showCentiseconds={distance <= 200 ? true : false} />

      <CarbonInfo/>
    </>
  )
}

// -------------------------------------------------------------
// Point d’entrée principal de l’application
// -------------------------------------------------------------
const rootElement = document.querySelector('#root')
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(<App />)
}
